import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { useMemo } from "react";
import { useRealmApp } from "../RealmApp";

function findAndAdd(sourceArray, destinationArray) {
  if (!sourceArray) return [];
  const resultArray = [];
  sourceArray.forEach((itemId) => {
    const itemFound = destinationArray.find((item) => item._id === itemId);
    if (itemFound) resultArray.push(itemFound);
  });
  return resultArray;
}

/*
* export type ZXReportItemType =
  | 'payment'
  | 'item'
  | 'user'
  | 'commission'
  | 'withdraw'
  | 'deposit'
  | 'tax';
*/
const useXReportItems = (xReport) => {
  const app = useRealmApp();
  const { xReportItems, loading } = useAllXReportItems(
    app.currentUser.id,
    xReport.storeId,
    xReport._id
  );
  const [payments, taxes, subcategories, users, withdrawals, commissions] =
    useMemo(() => {
      const payments = findAndAdd(
        xReport.payments,
        xReportItems.filter((item) => item.type === "payment")
      );
      const taxes = findAndAdd(
        xReport.taxes,
        xReportItems.filter((item) => item.type === "tax")
      );
      const subcategories = findAndAdd(
        xReport.subcategories,
        xReportItems.filter((item) => item.type === "item")
      );
      const users = findAndAdd(
        xReport.users,
        xReportItems.filter((item) => item.type === "user")
      );
      const withdrawals = findAndAdd(
        xReport.withdrawals,
        xReportItems.filter(
          (item) => item.type === "deposit" || item === "withdraw"
        )
      );
      const commissions = findAndAdd(
        xReport.commissions,
        xReportItems.filter((item) => item.type === "commission")
      );
      return [payments, taxes, subcategories, users, withdrawals, commissions];
    }, [xReport, xReportItems]);
  return {
    loading,
    payments,
    taxes,
    subcategories,
    users,
    withdrawals,
    commissions,
  };
};

export default useXReportItems;

function useAllXReportItems(ownerId, storeId, reportId) {
  const variables = {
    ownerId: `owner_id=${ownerId}&store_id=${storeId}`,
    reportId,
  };
  //
  const { data, loading, error } = useQuery(
    gql`
      query getXReportItems($ownerId: String!, $reportId: ObjectId!) {
        xReportItems(query: { owner_id: $ownerId, reportId: $reportId }) {
          _id
          amount
          amount2
          amount3
          code
          creationDatetime
          description
          groupDescription
          groupId
          itemId
          quantity
          quantity2
          quantity3
          reportId
          storeId
          terminalId
          terminalSerial
          type
          updateDatetime
        }
      }
    `,
    { variables }
  );
  //
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  let xReportItems = useMemo(() => data?.xReportItems ?? [], [data]);

  if (!Array.isArray(xReportItems)) {
    xReportItems = [xReportItems];
  }

  const sortedXReportItems = useMemo(() => {
    return xReportItems
      .slice()
      .sort((a, b) => new Date(b.updateDatetime) - new Date(a.updateDatetime));
  }, [xReportItems]);

  return { xReportItems: sortedXReportItems, loading };
}
